import {
  type EmailTemplateReadable,
  type LeadsContactsListData,
  type MailboxesTemplatesListData,
  type PaginatedEmailTemplateListReadable,
  type PaginatedReturnContactListReadable,
  type ReturnContactReadable,
  leadsContactsListInfiniteOptions,
  leadsRetrieveOptions,
  leadsRetrieveQueryKey,
  mailboxesSendEmailCreateMutation,
  mailboxesTemplatesListInfiniteOptions,
  mailboxesTemplatesRetrieveOptions
} from '@crm/api'
import { InfiniteSelect } from '@crm/components'
import { listTransformTemplates, userTransformResults } from '@crm/utils'
import { TipTapEditor } from '@shared/components/TipTapEditor'
import { useLocales } from '@shared/locales'
import { showApiErrors } from '@shared/utils'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { App, Button, Col, Flex, Form, Input, Row } from 'antd'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

interface Props {
  onCloseForm: () => void
}

export const MailForm = ({ onCloseForm }: Props) => {
  const { t } = useLocales()
  const { id: leadId } = useParams()
  const queryClient = useQueryClient()

  const { notification } = App.useApp()

  const [form] = Form.useForm()

  const [templateId, setTemplateId] = useState()

  const { setFieldsValue } = form

  const { data: lead } = useQuery({
    ...leadsRetrieveOptions({ path: { id: Number(leadId) } }),
    enabled: !!leadId
  })

  const { data: template } = useQuery({
    ...mailboxesTemplatesRetrieveOptions({
      path: { id: Number(templateId) }
    }),
    enabled: !!templateId
  })

  useEffect(() => {
    if (template) {
      setFieldsValue({
        subject: template.subject,
        message: template.message
      })
    }
  }, [template])

  const { mutate } = useMutation({
    ...mailboxesSendEmailCreateMutation(),
    onError: (e) => showApiErrors(e, form),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: leadsRetrieveQueryKey({ path: { id: Number(leadId) } })
      })
      onCloseForm()
      notification.success({
        message: t('success.success'),
        description: t('success.success_data_save')
      })
    }
  })

  const onSubmit = async () => {
    const values = await form.validateFields()

    mutate({ body: { ...values, message: values?.message } })
  }

  return (
    <Form form={form} onFinish={onSubmit} layout="vertical">
      <Form.Item
        name="contact"
        label={t('activities.to')}
        className="textarea-static-width"
        initialValue={lead?.contact?.id}
      >
        <InfiniteSelect<
          LeadsContactsListData,
          PaginatedReturnContactListReadable,
          ReturnContactReadable
        >
          className="select-min-width"
          popupMatchSelectWidth
          placeholder={t('logs.all_contacts')}
          getQueryFunction={leadsContactsListInfiniteOptions}
          getOptions={({ options }) => userTransformResults(options)}
          baseOptions={{
            path: {
              lead_id: String(leadId)
            },
            query: {
              preference: lead?.contact?.id ? [lead?.contact?.id] : undefined
            }
          }}
        />
      </Form.Item>

      <Row justify="space-between" align="middle">
        <Col span={16}>
          <Form.Item name="subject" label={t('activities.subject')}>
            <Input />
          </Form.Item>
        </Col>

        <Col span={7}>
          <Form.Item label={<></>}>
            <InfiniteSelect<
              MailboxesTemplatesListData,
              PaginatedEmailTemplateListReadable,
              EmailTemplateReadable
            >
              className="select-min-width"
              popupMatchSelectWidth
              placeholder={t('account_settings.email_templates')}
              getQueryFunction={mailboxesTemplatesListInfiniteOptions}
              onChange={(id) => setTemplateId(id)}
              getOptions={({ options }) => listTransformTemplates(options)}
            />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item
        name="message"
        label={t('activities.message')}
        className="textarea-static-width"
      >
        <TipTapEditor />
      </Form.Item>

      <Flex justify="space-between">
        <Button onClick={onCloseForm}>{t('buttons.cancel')}</Button>

        <Button type="primary" htmlType="submit">
          {t('buttons.save')}
        </Button>
      </Flex>
    </Form>
  )
}
